import { ArrowLeft, CheckCircle2, Check, Lock, Mail, Smartphone } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import AuthShell from "./AuthShell";
import { authService } from "@/services/apiServices";

type Step = 1 | 2 | 3 | "success";

const PASSWORD_RULES = [
  { key: "length", label: "Minimum 8 characters", test: (v: string) => v.length >= 8 },
  { key: "upper", label: "One uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { key: "number", label: "One number", test: (v: string) => /\d/.test(v) },
  { key: "special", label: "One special character", test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

function StepIndicator({ current }: { current: Step }) {
  const step = current === "success" ? 3 : current;
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={`h-2.5 w-2.5 rounded-full transition-colors ${
            n <= step ? "bg-primary-500" : "bg-slate-300"
          }`}
        />
      ))}
    </div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [identifier, setIdentifier] = useState("");
  const [identifierError, setIdentifierError] = useState("");
  const [apiError, setApiError] = useState("");

  // OTP Target info from backend
  const [targetEmail, setTargetEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  // OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(60);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // New password
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passError, setPassError] = useState("");

  const [loading, setLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (step !== 2) return;
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [step, countdown]);

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    const cleanId = identifier.trim();
    if (!cleanId) {
      setIdentifierError("Please enter your registered email address or mobile number.");
      return;
    }
    setIdentifierError("");
    setApiError("");
    setLoading(true);

    try {
      const res = await authService.requestResetOtp(cleanId);
      setLoading(false);

      if (res && res.success && res.data) {
        setTargetEmail(res.data.email);
        setMaskedEmail(res.data.maskedEmail || res.data.email);
        setStep(2);
        setCountdown(60);
        setCanResend(false);
      } else {
        setApiError(res.message || "Failed to send OTP. Please check your details.");
      }
    } catch (err: any) {
      setLoading(false);
      const msg = err.message || "Failed to send OTP. No account found with this details.";
      setApiError(msg);
      setIdentifierError(msg);
    }
  }

  function handleOtpChange(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  async function handleResend() {
    setOtp(["", "", "", "", "", ""]);
    setCountdown(60);
    setCanResend(false);
    setApiError("");
    setLoading(true);

    try {
      await authService.requestResetOtp(identifier.trim() || targetEmail);
      setLoading(false);
      otpRefs.current[0]?.focus();
    } catch (err: any) {
      setLoading(false);
      setApiError(err.message || "Failed to resend OTP. Please try again.");
    }
  }

  const strength = PASSWORD_RULES.filter((r) => r.test(newPass)).length;
  const strengthSegments = Array.from({ length: 4 }, (_, i) => i < strength);
  const strengthColor =
    strength <= 1 ? "bg-danger" : strength === 2 ? "bg-warning" : strength === 3 ? "bg-amber-500" : "bg-success";

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    const fullOtp = otp.join("");
    if (fullOtp.length < 6) {
      setApiError("Please enter the complete 6-digit OTP sent to your email.");
      return;
    }
    setApiError("");
    setLoading(true);

    try {
      const res = await authService.verifyResetOtp(targetEmail, fullOtp);
      setLoading(false);
      if (res && res.success) {
        setStep(3);
      } else {
        setApiError(res.message || "Invalid or expired OTP.");
      }
    } catch (err: any) {
      setLoading(false);
      setApiError(err.message || "Invalid or expired OTP. Please check the code.");
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setApiError("");
    const next: string[] = [];
    if (!PASSWORD_RULES.every((r) => r.test(newPass))) {
      next.push("Password does not meet all requirements.");
    }
    if (newPass !== confirmPass) {
      next.push("Passwords do not match.");
    }
    setPassError(next.join(" "));
    if (next.length) return;

    setLoading(true);
    try {
      const res = await authService.resetPassword(targetEmail, otp.join(""), newPass, confirmPass);
      setLoading(false);
      if (res && res.success) {
        setStep("success");
      } else {
        setApiError(res.message || "Failed to reset password.");
      }
    } catch (err: any) {
      setLoading(false);
      setApiError(err.message || "Failed to reset password. Please try again.");
    }
  }

  function renderStepContent() {
    if (step === 1) {
      return (
        <form onSubmit={handleSendOtp} noValidate>
          <h2 className="text-2xl font-semibold text-ink">Reset your password</h2>
          <p className="mt-1 text-sm text-ink-secondary">Enter your registered email address or mobile number</p>

          {apiError && (
            <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
              {apiError}
            </div>
          )}

          <div className="mt-6">
            <Input
              label="Email Address or Mobile Number"
              placeholder="Enter email or 10-digit mobile"
              prefixIcon={<Mail className="h-[18px] w-[18px]" />}
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setIdentifierError("");
                setApiError("");
              }}
              error={identifierError}
            />
          </div>

          <Button type="submit" fullWidth className="mt-6" loading={loading}>
            Send OTP to Email
          </Button>
        </form>
      );
    }

    if (step === 2) {
      return (
        <form onSubmit={handleVerifyOtp} noValidate>
          <h2 className="text-2xl font-semibold text-ink">Verify Email OTP</h2>
          <p className="mt-1 text-sm text-ink-secondary">
            A 6-digit OTP code was sent to <strong className="text-ink">{maskedEmail || targetEmail}</strong>
          </p>

          {apiError && (
            <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
              {apiError}
            </div>
          )}

          <div className="mt-8 flex justify-between gap-2">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  otpRefs.current[i] = el;
                }}
                value={digit}
                onChange={(e) => {
                  handleOtpChange(i, e.target.value);
                  setApiError("");
                }}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                inputMode="numeric"
                maxLength={1}
                className="h-12 w-12 rounded-lg border border-slate-300 text-center text-lg font-semibold text-ink focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                aria-label={`OTP digit ${i + 1}`}
              />
            ))}
          </div>

          <p className="mt-4 text-center text-[13px] text-ink-secondary">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                className="font-semibold text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                Resend OTP
              </button>
            ) : (
              <>
                Resend OTP in <span className="font-semibold text-ink">00:{String(countdown).padStart(2, "0")}</span>
              </>
            )}
          </p>

          <Button type="submit" fullWidth className="mt-6" loading={loading}>
            Verify OTP
          </Button>

          <button
            type="button"
            onClick={() => {
              setStep(1);
              setApiError("");
            }}
            className="mt-4 w-full text-center text-sm text-ink-secondary hover:text-ink cursor-pointer"
          >
            Change email/mobile
          </button>
        </form>
      );
    }

    if (step === 3) {
      return (
        <form onSubmit={handleResetPassword} noValidate>
          <h2 className="text-2xl font-semibold text-ink">Set new password</h2>
          <p className="mt-1 text-sm text-ink-secondary">Choose a strong password for your account</p>

          {apiError && (
            <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
              {apiError}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-4">
            <Input
              label="New Password"
              type="password"
              placeholder="Enter new password"
              prefixIcon={<Lock className="h-[18px] w-[18px]" />}
              value={newPass}
              onChange={(e) => {
                setNewPass(e.target.value);
                setPassError("");
                setApiError("");
              }}
              error={passError}
            />
            <Input
              label="Confirm Password"
              type="password"
              placeholder="Re-enter new password"
              prefixIcon={<Lock className="h-[18px] w-[18px]" />}
              value={confirmPass}
              onChange={(e) => {
                setConfirmPass(e.target.value);
                setPassError("");
                setApiError("");
              }}
            />
          </div>

          <div className="mt-5">
            <div className="flex gap-1.5">
              {strengthSegments.map((on, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${on ? strengthColor : "bg-slate-200"}`}
                />
              ))}
            </div>
            <ul className="mt-3 grid grid-cols-1 gap-1.5">
              {PASSWORD_RULES.map((rule) => {
                const pass = rule.test(newPass);
                return (
                  <li key={rule.key} className="flex items-center gap-2 text-[13px]">
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full ${
                        pass ? "bg-success-bg text-success" : "bg-slate-100 text-ink-muted"
                      }`}
                    >
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className={pass ? "text-ink" : "text-ink-muted"}>{rule.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <Button type="submit" fullWidth className="mt-6" loading={loading}>
            Reset Password
          </Button>
        </form>
      );
    }

    return (
      <div className="flex flex-col items-center py-6 text-center animate-fadeIn">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success-bg text-success">
          <CheckCircle2 className="h-9 w-9" />
        </span>
        <h2 className="mt-5 text-xl font-semibold text-ink">Password reset successful</h2>
        <p className="mt-1.5 text-sm text-ink-secondary">You can now sign in with your new password</p>
        <Button fullWidth className="mt-7" onClick={() => navigate("/login")}>
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <AuthShell>
      {step !== "success" && <StepIndicator current={step} />}
      <div className="mt-5">{renderStepContent()}</div>
      {step === 1 && (
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to login
        </button>
      )}
    </AuthShell>
  );
}